import { useEffect } from "react";
import { useQuoteModal } from "../contexts/QuoteModalContext";
import {
  FaLanguage,
  FaHeadphones,
  FaEdit,
  FaChalkboardTeacher,
  FaFileAudio,
  FaCheckCircle,
} from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";

const Services = () => {
  const { openModal } = useQuoteModal();
  
  useEffect(() => {
    AOS.init({
      duration: 1000,
      easing: "ease-in-out",
      once: true,
      offset: 100,
    });
  }, []);

  const services = [
    {
      title: "Corporate Training",
      description:
        "Empower your workforce with practical language skills for global business, client interactions, and international operations.",
      icon: <FaChalkboardTeacher />,
      features: [
        "Business & professional communication",
        "Industry-specific language training",
        "Customized corporate programs",
        "Team & leadership language upskilling",
        "Global workplace readiness",
      ],
    },
    {
      title: "Certified Translation",
      description:
        "Embassy-ready, error-free translations for legal, academic, and corporate documents — fast, accurate, and fully compliant.",
      icon: <FaLanguage />,
      features: [
        "Certified & notarized translations",
        "Legal, academic & corporate documents",
        "Embassy & immigration compliant",
        "Strict quality checks",
        "Fast turnaround",
      ],
    },
    {
      title: "Interpretation",
      description:
        "Professional interpreters for real-time communication — on-site or remote — ensuring clarity and precision.",
      icon: <FaHeadphones />,
      features: [
        "Simultaneous interpretation",
        "Consecutive interpretation",
        "On-site & remote support",
        "Meetings, conferences & events",
        "Clear and accurate communication",
      ],
    },
    {
      title: "Proofreading & Editing",
      description:
        "Polish your documents to perfection — grammar, tone, structure, and terminology refined for professional impact.",
      icon: <FaEdit />,
      features: [
        "Grammar & language correction",
        "Tone & style refinement",
        "Academic & corporate editing",
        "Terminology consistency",
        "Professional-quality output",
      ],
    },
    {
      title: "Transcription",
      description:
        "Accurate, reliable transcripts for interviews, webinars, meetings, and legal or academic recordings.",
      icon: <FaFileAudio />,
      features: [
        "Audio & video transcription",
        "Meetings, interviews & webinars",
        "Legal & academic recordings",
        "High accuracy assurance",
        "Confidential handling",
      ],
    }
  ];

  return (
    <div className="min-h-screen bg-canvas pb-20 text-ink overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-primary/10 py-10">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${process.env.PUBLIC_URL}/Services.png')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/65 to-black/25" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/25 via-transparent to-primary/25" />

        <div className="relative mx-auto max-w-6xl px-4 py-24 text-center">
          <h1
            data-aos="fade-down"
            className="text-5xl font-bold sm:text-6xl lg:text-7xl text-white drop-shadow-lg"
          >
            Professional Language Services
          </h1>
          <p
            data-aos="fade-up"
            data-aos-delay="100"
            className="mt-6 max-w-3xl mx-auto text-lg text-white/90 drop-shadow-md"
          >
            At The Lingua Alliance, we don’t just teach languages — we power
            communication. Our expert linguists, certified translators, and
            industry-trained interpreters deliver trusted language solutions for
            businesses, institutions, and individuals worldwide.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-white via-canvas/30 to-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16" data-aos="fade-up">
            <span className="inline-flex rounded-full bg-gradient-to-r from-primary/10 to-primary-soft/10 px-4 sm:px-5 py-1.5 sm:py-2 text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] sm:tracking-[0.25em] text-primary border border-primary/20">
              Our Services
            </span>
            <h2 className="mt-5 sm:mt-6 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold">
              Comprehensive Language Solutions
            </h2>
            <p className="mt-4 sm:mt-5 max-w-2xl mx-auto text-sm sm:text-base lg:text-lg text-ink-muted px-4">
              Whether you need precise translations, seamless interpretation, or
              professional language support — we've got you covered.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-5 sm:gap-6 lg:gap-8">
            {services.map((service, index) => (
              <div
                key={service.title}
                data-aos="fade-up"
                data-aos-delay={index * 100}
                className="group relative w-full rounded-2xl sm:rounded-3xl border border-primary/10 bg-gradient-to-br from-slate-100 to-slate-200 p-6 sm:p-8 shadow-[0_10px_40px_rgba(0,0,0,0.08)] transition-all duration-500 hover:-translate-y-3 hover:shadow-[0_30px_80px_rgba(18,10,56,0.25)] md:w-[calc(50%-12px)] lg:w-[calc(33.333%-22px)]"
              >
                <div className="inline-flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-xl sm:rounded-2xl bg-gradient-to-br from-primary to-primary-soft text-white mb-5 sm:mb-6 shadow-lg text-xl sm:text-2xl">
                  {service.icon}
                </div>

                <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-primary">
                  {service.title}
                </h3>

                <p className="text-xs sm:text-sm text-ink-muted mb-5 sm:mb-6">
                  {service.description}
                </p>

                <ul className="space-y-2 sm:space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex gap-2 sm:gap-3 text-xs sm:text-sm text-ink-muted">
                      <span className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-gradient-to-r from-primary to-primary-soft flex items-center justify-center flex-shrink-0">
                        <FaCheckCircle className="text-white text-[8px] sm:text-[10px]" />
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-white via-primary/5 to-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <div
            data-aos="zoom-in"
            className="rounded-2xl sm:rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/10 via-white to-white p-8 sm:p-10 text-center shadow-xl"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 bg-gradient-to-r from-primary to-primary-soft bg-clip-text text-transparent">
              Ready to Get Started?
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-ink-muted mb-6 sm:mb-8 px-4">
              Get your project started in minutes with our professional language
              services.
            </p>
            <button
              onClick={() => openModal()}
              className="inline-block rounded-full bg-gradient-to-r from-primary via-primary-soft to-primary px-8 sm:px-10 py-3 sm:py-4 text-xs sm:text-sm font-bold uppercase text-white shadow-lg hover:-translate-y-1 transition"
            >
              Get a Free Quote →
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
